import * as React from "react";
import { useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { FileUpload } from "@/components/FileUpload";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { ArrowLeft, CirclePlus, Download } from "lucide-react";
import { Input } from "@/components/ui/input";

import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DatePicker from "@/components/ui/DatePicker";
import { Spinner } from "@/components/ui/spinner";

// Services and Utils
import { getDivDetailsed, postDivPay } from "@/services/tax-obligations";
// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";
// Components
import { FilePreviewModal } from "@/components/FilePreviewModal";

interface Dividend {
  id: string;
  dividendPlanId: string;
  dividend: number;
  phase: number;
  dividendDate: string;
  docFile: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DividendResponse {
  dividends: Dividend[];
  docFile: string;
}
interface EditInfoProps {
  onClose?: () => void;
  open: boolean;
  term: number;
}
import type { FC } from "react";

export const Route = createFileRoute(
  "/_layout-private/report/obligation/dividend/"
)({
  component: RouteComponent,
});

function formatNumber(num: number) {
  return num?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function RouteComponent() {
  const navigate = useNavigate();
  const { delegationReport } = useAccessPermission();
  const [AddNew, setAddNew] = React.useState(false);
  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.DIVIDEND_DISTRIBUTION_REPORT);

  const dividendPlanId = localStorage
    .getItem("divId")
    ?.replace(/^"(.+)"$/, "$1");
  const {
    data: divDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["div ", dividendPlanId],
    queryFn: () => getDivDetailsed({ dividendPlanId }),
    enabled: !!dividendPlanId,
  });
  if (isLoading) {
    return <DinamixSkeletonTable />;
  }
  if (error) {
    return <div>Error loading dividend details</div>;
  }
  const TheData: DividendResponse = divDetails.data;

  const toAdd = () => {
    setAddNew(true);
  };

  return (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານແຜນເງິນປັນຜົນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/obligation",
                search: {
                  active: "dividends" as const,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex gap-1 items-center mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>ລາຍງານມອບເງິນປັນຜົນ</CardTitle>
              <CardDescription>ລາຍການລາຍງານເງິນປັນຜົນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>

          <div className="flex gap-x-2 justify-end my-5 w-full">
            {delegationReport.isWrite && (
              <div>
                <Button className="flex gap-2 items-center" onClick={toAdd}>
                  <CirclePlus size={16} /> ເພີ່ມລາຍງານ
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <DinamixSkeletonTable />
          ) : (
            <div>
              <Table className="my-4 w-full">
                <TableHeader>
                  <TableRow className="[&>*]:font-bold [&>*]:text-black [&>*]:bg-gray-100 [&>*]:border">
                    <TableHead>ຈຳນວນງວດ </TableHead>
                    <TableHead>ຈຳນວນເງິນປັນຜົນຕົວຈິງ</TableHead>
                    <TableHead> ວັນທີຈ່າຍ </TableHead>
                    <TableHead> ເອກະສານຕິດຄັດ </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className=" [&>*]:text-black [&>*]:border">
                  {TheData?.dividends?.map((invoice: Dividend) => (
                    <TableRow key={invoice.id} className="cursor-pointer">
                      <TableCell className="font-medium">
                        ຈຳນວນງວດ {invoice?.phase}
                      </TableCell>
                      <TableCell className="font-medium text-right">
                        {formatNumber(invoice?.dividend)}
                      </TableCell>
                      <TableCell>
                        {moment(invoice?.dividendDate).format("DD/MM/YYYY")}
                      </TableCell>
                      {delegationReport.isDownload && (
                        <TableCell className="text-blue-500 underline">
                          <a
                            href={S3_BUCKET_LINK + invoice?.docFile}
                            download={S3_BUCKET_LINK + invoice?.docFile}
                            target="_blank"
                            rel="noreferrer"
                          >
                            ເອກະສານຕິດຄັດ
                          </a>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-100 cursor-pointer">
                    <TableCell className="font-bold border">
                      ເງິນປັນຜົນຕົວຈິງ
                    </TableCell>
                    <TableCell className="font-bold text-right border">
                      {formatNumber(divDetails?.data?.actualDividend)}{" "}
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow className="bg-gray-100 cursor-pointer">
                    <TableCell className="font-bold border">
                      ແຜນມອບເງິນປັນຜົນ
                    </TableCell>
                    <TableCell className="font-bold text-right border">
                      {formatNumber(divDetails?.data?.plannedDividend)}{" "}
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                  <TableRow className="bg-gray-100 cursor-pointer">
                    <TableCell className="font-bold border">
                      ຄ້າງມອບເງິນປັນຜົນ
                    </TableCell>
                    <TableCell
                      className={`font-bold text-right border ${
                        divDetails?.data?.actualDividend -
                          divDetails?.data?.plannedDividend <
                        0
                          ? "text-red-500"
                          : divDetails?.data?.actualDividend -
                              divDetails?.data?.plannedDividend >
                            0
                          ? "text-green-500"
                          : "text-black"
                      }`}
                    >
                      {formatNumber(
                        divDetails?.data?.actualDividend -
                          divDetails?.data?.plannedDividend
                      )}
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {TheData?.docFile ? (
            <div className="flex justify-start mt-4">
              <a
                href={S3_BUCKET_LINK + TheData?.docFile}
                download={S3_BUCKET_LINK + TheData?.docFile}
                target="_blank"
                rel="noreferrer"
              >
                <Button className="flex gap-2 items-center">
                  <Download className="w-4 h-4" />
                  <span>ດາວໂຫຼດເອກະສານຕິດຄັດ</span>
                </Button>
              </a>
            </div>
          ) : null}
        </CardContent>
        {AddNew ? (
          <Edit
            open={AddNew}
            onClose={() => setAddNew(false)}
            term={TheData?.dividends?.length + 1}
          />
        ) : null}
      </Card>
    </section>
  );
}

const Edit: FC<EditInfoProps> = ({ open, onClose, term }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dividendPlanId: localStorage.getItem("divId") || "",
    dividend: "",
    dividendDate: "",
    docFile: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDividendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all commas first
    value = value.replace(/,/g, "");

    // Allow only numbers and one decimal point
    if (!/^\d*\.?\d{0,2}$/.test(value) && value !== "") {
      return;
    }

    // Format with commas if there's a value
    if (value) {
      const parts = value.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      value = parts.join(".");
    }

    setFormData((prev) => ({ ...prev, dividend: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.dividend || !formData.dividendDate || !formData.docFile) {
      toast({
        title: "ຜິດພາດ",
        description: "ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const data = {
        dividendPlanId: formData.dividendPlanId,
        dividend: Number(formData.dividend.replace(/,/g, "")),
        dividendDate: formData.dividendDate,
        docFile: formData.docFile,
      };

      await postDivPay(data);
      toast({
        title: "ສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["dividends"] });
      navigate({
        to: "/report/obligation",
        search: { active: "dividends" as const, startYear: 0, endYear: 0 },
      });
      onClose?.();
    } catch (error) {
      console.error("Error submitting dividend:", error);
      toast({
        title: "ຜິດພາດ",
        description: "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-center text-green-700">
            ແຜນການຊຳລະຈ່າຍຄ່າງວດ {term}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>
              ຈຳນວນເງິນຕົວຈິງ <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              name="dividend"
              placeholder="1,000,000"
              className="text-right rounded-md"
              value={formData.dividend}
              onChange={handleDividendChange}
              inputMode="decimal"
            />
          </div>

          <div className="flex flex-col">
            <DatePicker
              label={
                <p>
                  ວັນທີເດືອນປີຈ່າຍ <span className="text-red-500">*</span>
                </p>
              }
              value={formData.dividendDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, dividendDate: value }))
              }
            />
          </div>

          <div className="space-y-2 w-full">
            <FileUpload
              currentFile={formData.docFile}
              onFileUpload={(value) =>
                setFormData((prev) => ({ ...prev, docFile: value }))
              }
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              ຍົກເລີກ
            </Button>
            <Button
              disabled={isSubmitting}
              type="submit"
              className="text-white bg-green-700"
            >
              {isSubmitting ? <Spinner className="text-white" /> : "ບັນທຶກ"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
